import { DEFAULT_TYPOGRAPHY, type Typography } from '@gb-ticket/shared-types';
import { useEffect, useState, type ReactNode } from 'react';

import { router } from '@/router';
import { useMe } from '@/platform/auth/use-me';
import { usePublicBrand } from '@/platform/brand/use-public-brand';
import { useOwnProfile } from '@/platform/features/users/use-own-profile';

import {
  LOGIN_PATH,
  applyTypography,
  browserTypographyStorage,
  clearTypography,
  resolveTypography,
  writeTypography,
} from './typography';

function brandTypography(
  brand:
    | {
        displayFont: Typography['displayFont'];
        displayFontSize: Typography['displayFontSize'];
      }
    | undefined,
): Typography | null {
  if (!brand) {
    return null;
  }
  return { displayFont: brand.displayFont, displayFontSize: brand.displayFontSize };
}

/**
 * Keeps the document on the resolved pair.
 * The login page uses the public brand only. A signed-out session drops the stored pair.
 */
export function TypographyProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const brand = usePublicBrand();
  const profile = useOwnProfile(Boolean(me.data));
  const [pathname, setPathname] = useState(() => router.state.location.pathname);

  useEffect(() => {
    return router.subscribe((state) => {
      setPathname(state.location.pathname);
    });
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const brandPair = brandTypography(brand.data);
    const storage = browserTypographyStorage();

    if (pathname === LOGIN_PATH) {
      applyTypography(root, brandPair ?? { ...DEFAULT_TYPOGRAPHY });
      return;
    }

    if (!me.isPending && !me.data) {
      clearTypography(storage);
      applyTypography(root, brandPair ?? { ...DEFAULT_TYPOGRAPHY });
      return;
    }

    if (!me.data || profile.isPending) {
      return;
    }

    const followsTenant = !profile.data?.displayFont || !profile.data.displayFontSize;
    if (followsTenant && brand.isPending) {
      return;
    }

    const pair = resolveTypography(profile.data, brandPair);
    writeTypography(storage, pair);
    applyTypography(root, pair);
  }, [
    pathname,
    me.isPending,
    me.data,
    profile.isPending,
    profile.data,
    brand.isPending,
    brand.data,
  ]);

  return children;
}
